/**
 * Block-driven category carousel of the "Main collection V2 + categories" section.
 *
 * Top-level items are plain links. Second-level items (data-filter-link) belong to the same parent category, so
 * picking one re-renders this section for that subcategory's collection through the Section Rendering API and swaps
 * it in place — the products change without a page load, the URL follows with pushState, and back/forward replays
 * it. The re-rendered section brings its own carousel with the new item active, so this element is replaced on
 * every swap and keeps no state of its own beyond the request in flight.
 */
if (!window.customElements.get('collection-category-nav')) {
  class CollectionCategoryNav extends HTMLElement {
    #abortController = null;
    #onClick = this.#handleClick.bind(this);
    #onPopState = this.#handlePopState.bind(this);

    connectedCallback() {
      this.addEventListener('click', this.#onClick);
      window.addEventListener('popstate', this.#onPopState);
      this.#centerActiveItem();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      window.removeEventListener('popstate', this.#onPopState);
    }

    get #list() {
      return this.querySelector('.collection-category-nav__list');
    }

    #handleClick(event) {
      const link = event.target.closest('a[data-filter-link]');

      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      event.preventDefault();

      // Tapping the active subcategory again lifts it: back to the whole parent category, nothing selected.
      const isDeselect = link.getAttribute('aria-current') === 'page';

      if (isDeselect && !this.hasAttribute('parent-url')) {
        return;
      }

      // Carry the visitor's own view and sort over; filters and page belong to the collection being left.
      const url = new URL(isDeselect ? this.getAttribute('parent-url') : link.href, window.location.origin);
      const currentParams = new URLSearchParams(window.location.search);

      ['view', 'sort_by'].forEach((name) => {
        if (currentParams.has(name)) {
          url.searchParams.set(name, currentParams.get(name));
        }
      });

      this.#markActive(isDeselect ? null : link);
      this.#navigate(url, true);
    }

    #handlePopState() {
      const pathname = this.#normalizePath(window.location.pathname);

      if (pathname === this.#normalizePath(new URL(this.getAttribute('collection-url'), window.location.origin).pathname)) {
        return;
      }

      // Only entries this carousel could have produced; anything else is left to the browser.
      const knownPaths = Array.from(this.querySelectorAll('a[data-filter-link]'), (link) => this.#normalizePath(new URL(link.href).pathname));

      if (this.hasAttribute('parent-url')) {
        knownPaths.push(this.#normalizePath(new URL(this.getAttribute('parent-url'), window.location.origin).pathname));
      }

      if (knownPaths.includes(pathname)) {
        this.#navigate(new URL(window.location.href), false);
      }
    }

    async #navigate(url, pushToHistory) {
      const sectionId = this.getAttribute('section-id');
      const section = document.getElementById(`shopify-section-${sectionId}`);

      if (!section) {
        window.location.assign(url.toString());
        return;
      }

      // The banner above carries the breadcrumb and description, so it is re-rendered alongside.
      const bannerIds = Array.from(document.querySelectorAll('.shopify-section--collection-banner'), (element) => element.id.replace('shopify-section-', ''));
      const fetchUrl = new URL(url);

      fetchUrl.searchParams.delete('page');
      fetchUrl.searchParams.set('sections', [sectionId, ...bannerIds].join(','));

      // Render the subcategory with the template this page uses, whatever template its collection is assigned.
      if (this.getAttribute('view') && !fetchUrl.searchParams.has('view')) {
        fetchUrl.searchParams.set('view', this.getAttribute('view'));
      }

      this.#abortController?.abort();
      const abortController = (this.#abortController = new AbortController());

      section.setAttribute('aria-busy', 'true');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const response = await fetch(fetchUrl.toString(), { signal: abortController.signal });

        if (!response.ok) {
          throw new Error(`Section rendering failed with ${response.status}`);
        }

        const sections = await response.json();

        if (!sections[sectionId]) {
          throw new Error('Section missing from the response');
        }

        if (pushToHistory) {
          window.history.pushState({ collectionCategoryNav: true }, '', url.toString());
        }

        const previousTitle = this.getAttribute('collection-title');
        const scrollLeft = this.#list?.scrollLeft ?? 0;

        this.#replaceSection(section, sections[sectionId]);

        bannerIds.forEach((bannerId) => {
          if (sections[bannerId]) {
            this.#updateBanner(document.getElementById(`shopify-section-${bannerId}`), sections[bannerId]);
          }
        });

        // Same items in the same order, so the row stays where the visitor left it instead of jumping.
        const newNav = section.querySelector('collection-category-nav');
        newNav?.querySelector('.collection-category-nav__list')?.scrollTo({ left: scrollLeft, behavior: 'instant' });

        const newTitle = newNav?.getAttribute('collection-title');

        if (previousTitle && newTitle && document.title.includes(previousTitle)) {
          document.title = document.title.replace(previousTitle, newTitle);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        // Whatever went wrong, a regular page load still gets the visitor to the subcategory.
        window.location.assign(url.toString());
      } finally {
        // A newer pick took over this request; the loading state is now that one's to end.
        if (!abortController.signal.aborted) {
          section.removeAttribute('aria-busy');
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }
      }
    }

    #replaceSection(section, html) {
      const newSection = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');

      if (newSection) {
        section.replaceChildren(...document.importNode(newSection, true).childNodes);
      }
    }

    // Only the text parts are swapped: the banner also holds the Trustpilot widget, which a full swap would break.
    #updateBanner(banner, html) {
      if (!banner) {
        return;
      }

      const newBanner = new DOMParser().parseFromString(html, 'text/html');

      ['.breadcrumb', '.collection-description', 'h1'].forEach((selector) => {
        const currentElement = banner.querySelector(selector);
        const newElement = newBanner.querySelector(selector);

        if (currentElement && newElement) {
          currentElement.replaceWith(document.importNode(newElement, true));
        }
      });
    }

    #markActive(link) {
      this.querySelectorAll('.collection-category-nav__item').forEach((item) => {
        item.classList.toggle('is-active', item === link);
        item === link ? item.setAttribute('aria-current', 'page') : item.removeAttribute('aria-current');
      });
    }

    #centerActiveItem() {
      const list = this.#list;
      const activeItem = list?.querySelector('.collection-category-nav__item.is-active');

      if (!activeItem || list.scrollWidth <= list.clientWidth) {
        return;
      }

      list.scrollTo({ left: activeItem.offsetLeft - (list.clientWidth - activeItem.offsetWidth) / 2, behavior: 'instant' });
    }

    #normalizePath(pathname) {
      return decodeURIComponent(pathname).replace(/\/$/, '').toLowerCase();
    }
  }

  window.customElements.define('collection-category-nav', CollectionCategoryNav);
}
